import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { Input } from '../components/ui/Input';
import { Toggle } from '../components/ui/Toggle';
import { Badge } from '../components/ui/Badge';
import { Save, Building2, Users, Bell, DollarSign, FileText, BarChart3, Plug, Image, Palette, Mail, Shield, Upload, Database } from 'lucide-react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
type SettingsSection = 'company' | 'buyer-experience' | 'users' | 'notifications' | 'mortgage' | 'pricing' | 'pdf' | 'integrations' | 'analytics' | 'content';
export function SettingsPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const {
    builderId
  } = useParams<{
    builderId: string;
  }>();
  // Get the base path for navigation
  const basePath = builderId ? `/${builderId}` : '';
  const getActiveSection = (): SettingsSection => {
    if (location.pathname.includes('/mortgage')) return 'mortgage';
    if (location.pathname.includes('/general')) return 'company';
    return 'company';
  };
  const [activeSection, setActiveSection] = useState<SettingsSection>(getActiveSection());
  const [hasChanges, setHasChanges] = useState(false);
  // Notification states
  const [notifications, setNotifications] = useState({
    newBuyer: true,
    tourCompletion: true,
    optionSelections: true,
    dailyDigest: true,
    weeklyReport: true
  });
  const sections = [{
    id: 'company' as const,
    label: 'Company Profile',
    icon: Building2
  }, {
    id: 'buyer-experience' as const,
    label: 'Buyer Experience',
    icon: Palette
  }, {
    id: 'users' as const,
    label: 'User Management',
    icon: Users
  }, {
    id: 'notifications' as const,
    label: 'Notifications',
    icon: Bell
  }, {
    id: 'mortgage' as const,
    label: 'Mortgage Calculator',
    icon: DollarSign
  }, {
    id: 'pricing' as const,
    label: 'Pricing & Display',
    icon: DollarSign
  }, {
    id: 'pdf' as const,
    label: 'PDF Summaries',
    icon: FileText
  }, {
    id: 'integrations' as const,
    label: 'Integrations',
    icon: Plug
  }, {
    id: 'analytics' as const,
    label: 'Analytics & Tracking',
    icon: BarChart3
  }, {
    id: 'content' as const,
    label: 'Content Management',
    icon: Image
  }];
  const handleSectionChange = (section: SettingsSection) => {
    setActiveSection(section);
    if (section === 'mortgage') {
      navigate(`${basePath}/settings/mortgage`);
    } else {
      navigate(`${basePath}/settings/general`);
    }
  };
  const handleSave = () => {
    console.log('Saving settings...');
    setHasChanges(false);
  };
  const handleNotificationToggle = (key: keyof typeof notifications) => {
    setNotifications(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
    setHasChanges(true);
  };
  const renderContent = () => {
    switch (activeSection) {
      case 'company':
        return <CompanyProfileSettings onChange={() => setHasChanges(true)} data-id="element-6779" />;
      case 'buyer-experience':
        return <BuyerExperienceSettings onChange={() => setHasChanges(true)} data-id="element-6780" />;
      case 'users':
        return <UserManagementSettings onChange={() => setHasChanges(true)} data-id="element-6781" />;
      case 'notifications':
        return <NotificationSettings notifications={notifications} onToggle={handleNotificationToggle} data-id="element-6782" />;
      case 'mortgage':
        return <MortgageCalculatorSettings onChange={() => setHasChanges(true)} data-id="element-6783" />;
      case 'pricing':
        return <PricingDisplaySettings onChange={() => setHasChanges(true)} data-id="element-6784" />;
      case 'pdf':
        return <PDFSummarySettings onChange={() => setHasChanges(true)} data-id="element-6785" />;
      case 'integrations':
        return <IntegrationSettings onChange={() => setHasChanges(true)} data-id="element-6786" />;
      case 'analytics':
        return <AnalyticsTrackingSettings onChange={() => setHasChanges(true)} data-id="element-6787" />;
      case 'content':
        return <ContentManagementSettings onChange={() => setHasChanges(true)} data-id="element-6788" />;
      default:
        return null;
    }
  };
  return <div className="flex gap-6 max-w-7xl mx-auto h-[calc(100vh-8rem)]" data-id="element-6789">
      {/* Sidebar Navigation */}
      <div className="w-64 flex-shrink-0" data-id="element-6790">
        <Card className="h-full overflow-y-auto" noPadding data-id="element-6791">
          <div className="p-4 border-b border-slate-200" data-id="element-6792">
            <h2 className="font-semibold text-slate-900" data-id="element-6793">Settings</h2>
          </div>
          <nav className="p-2" data-id="element-6794">
            {sections.map(section => {
            const Icon = section.icon;
            return <button key={section.id} onClick={() => handleSectionChange(section.id)} className={`
                    w-full flex items-center gap-3 px-3 py-2 rounded-md text-sm transition-colors
                    ${activeSection === section.id ? 'bg-primary-50 text-primary-700 font-medium' : 'text-slate-600 hover:bg-slate-50 hover:text-slate-900'}
                  `} data-id="element-6795">
                  <Icon className="h-4 w-4" data-id="element-6796" />
                  {section.label}
                </button>;
          })}
          </nav>
        </Card>
      </div>

      {/* Main Content */}
      <div className="flex-1 overflow-y-auto" data-id="element-6797">
        <div className="space-y-6" data-id="element-6798">
          {/* Save Bar */}
          {hasChanges && <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 flex items-center justify-between" data-id="element-6799">
              <p className="text-sm text-amber-800 font-medium" data-id="element-6800">
                You have unsaved changes
              </p>
              <Button onClick={handleSave} leftIcon={<Save className="h-4 w-4" data-id="element-6802" />} data-id="element-6801">
                Save Changes
              </Button>
            </div>}

          {renderContent()}
        </div>
      </div>
    </div>;
}
// Company Profile Settings - BRANDING FIRST
function CompanyProfileSettings({
  onChange
}: {
  onChange: () => void;
}) {
  return <div className="space-y-6" data-id="element-6803">
      <div data-id="element-6804">
        <h1 className="text-2xl font-bold text-slate-900" data-id="element-6805">Company Profile</h1>
        <p className="text-slate-500" data-id="element-6806">
          Manage your company information and branding
        </p>
      </div>

      {/* BRANDING SECTION FIRST */}
      <Card data-id="element-6807">
        <CardHeader data-id="element-6808">
          <CardTitle data-id="element-6809">Branding</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4" data-id="element-6810">
          <div data-id="element-6811">
            <label className="block text-sm font-medium text-slate-700 mb-2" data-id="element-6812">
              Company Logo
            </label>
            <div className="flex items-center gap-4" data-id="element-6813">
              <div className="h-20 w-20 bg-slate-100 rounded-lg flex items-center justify-center border border-slate-200" data-id="element-6814">
                <Building2 className="h-8 w-8 text-slate-400" data-id="element-6815" />
              </div>
              <Button variant="outline" leftIcon={<Upload className="h-4 w-4" data-id="element-6817" />} data-id="element-6816">
                Upload Logo
              </Button>
            </div>
            <p className="text-xs text-slate-500 mt-2" data-id="element-6818">
              Recommended: PNG or SVG, 500x500px minimum
            </p>
          </div>

          <div className="grid grid-cols-2 gap-4" data-id="element-6819">
            <div data-id="element-6820">
              <label className="block text-sm font-medium text-slate-700 mb-2" data-id="element-6821">
                Primary Brand Color
              </label>
              <div className="flex items-center gap-3" data-id="element-6822">
                <input type="color" defaultValue="#06b6d4" className="h-10 w-20 rounded border border-slate-300" onChange={onChange} data-id="element-6823" />
                <Input defaultValue="#06b6d4" onChange={onChange} data-id="element-6824" />
              </div>
            </div>
            <div data-id="element-6825">
              <label className="block text-sm font-medium text-slate-700 mb-2" data-id="element-6826">
                Secondary Brand Color
              </label>
              <div className="flex items-center gap-3" data-id="element-6827">
                <input type="color" defaultValue="#334155" className="h-10 w-20 rounded border border-slate-300" onChange={onChange} data-id="element-6828" />
                <Input defaultValue="#334155" onChange={onChange} data-id="element-6829" />
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* BASIC INFORMATION SECOND */}
      <Card data-id="element-6830">
        <CardHeader data-id="element-6831">
          <CardTitle data-id="element-6832">Basic Information</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4" data-id="element-6833">
          <Input label="Company Name" defaultValue="Brookfield" onChange={onChange} data-id="element-6834" />
          <div className="grid grid-cols-2 gap-4" data-id="element-6835">
            <Input label="Phone Number" defaultValue="(555) 123-4567" onChange={onChange} data-id="element-6836" />
            <Input label="Email" defaultValue="info@brookfield.com" onChange={onChange} data-id="element-6837" />
          </div>
          <Input label="Address" defaultValue="123 Builder Lane, City, ST 12345" onChange={onChange} data-id="element-6838" />
          <div data-id="element-6839">
            <label className="block text-sm font-medium text-slate-700 mb-2" data-id="element-6840">
              Company Description
            </label>
            <textarea className="w-full px-3 py-2 border border-slate-300 rounded-md focus:ring-primary-500 focus:border-primary-500" rows={4} defaultValue="Building quality homes since 1995..." onChange={onChange} data-id="element-6841" />
          </div>
        </CardContent>
      </Card>

      <Card data-id="element-6842">
        <CardHeader data-id="element-6843">
          <CardTitle data-id="element-6844">Social Media</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4" data-id="element-6845">
          <Input label="Facebook" placeholder="https://facebook.com/..." onChange={onChange} data-id="element-6846" />
          <Input label="Instagram" placeholder="https://instagram.com/..." onChange={onChange} data-id="element-6847" />
          <Input label="LinkedIn" placeholder="https://linkedin.com/..." onChange={onChange} data-id="element-6848" />
          <Input label="YouTube" placeholder="https://youtube.com/..." onChange={onChange} data-id="element-6849" />
        </CardContent>
      </Card>
    </div>;
}
// Buyer Experience Settings
function BuyerExperienceSettings({
  onChange
}: {
  onChange: () => void;
}) {
  return <div className="space-y-6" data-id="element-6850">
      <div data-id="element-6851">
        <h1 className="text-2xl font-bold text-slate-900" data-id="element-6852">Buyer Experience</h1>
        <p className="text-slate-500" data-id="element-6853">
          Customize the buyer journey and interface
        </p>
      </div>

      <Card data-id="element-6854">
        <CardHeader data-id="element-6855">
          <CardTitle data-id="element-6856">Welcome Message</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4" data-id="element-6857">
          <div data-id="element-6858">
            <label className="block text-sm font-medium text-slate-700 mb-2" data-id="element-6859">
              Greeting Text
            </label>
            <textarea className="w-full px-3 py-2 border border-slate-300 rounded-md focus:ring-primary-500 focus:border-primary-500" rows={3} defaultValue="Welcome to your digital showhome experience! Explore our models and customize your dream home." onChange={onChange} data-id="element-6860" />
          </div>
        </CardContent>
      </Card>

      <Card data-id="element-6861">
        <CardHeader data-id="element-6862">
          <CardTitle data-id="element-6863">Tour Flow Preferences</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4" data-id="element-6864">
          <div className="flex items-center justify-between" data-id="element-6865">
            <div data-id="element-6866">
              <p className="font-medium text-slate-900" data-id="element-6867">Guided Tour Mode</p>
              <p className="text-sm text-slate-500" data-id="element-6868">
                Walk buyers through a structured experience
              </p>
            </div>
            <Toggle defaultChecked onChange={onChange} data-id="element-6869" />
          </div>
          <div className="flex items-center justify-between" data-id="element-6870">
            <div data-id="element-6871">
              <p className="font-medium text-slate-900" data-id="element-6872">
                Allow Free Exploration
              </p>
              <p className="text-sm text-slate-500" data-id="element-6873">
                Let buyers navigate at their own pace
              </p>
            </div>
            <Toggle defaultChecked onChange={onChange} data-id="element-6874" />
          </div>
          <div className="flex items-center justify-between" data-id="element-6875">
            <div data-id="element-6876">
              <p className="font-medium text-slate-900" data-id="element-6877">
                Show Progress Indicator
              </p>
              <p className="text-sm text-slate-500" data-id="element-6878">
                Display completion percentage
              </p>
            </div>
            <Toggle defaultChecked onChange={onChange} data-id="element-6879" />
          </div>
        </CardContent>
      </Card>

      <Card data-id="element-6880">
        <CardHeader data-id="element-6881">
          <CardTitle data-id="element-6882">Lead Capture Form</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4" data-id="element-6883">
          <div className="space-y-3" data-id="element-6884">
            {[{
            field: 'Name',
            required: true
          }, {
            field: 'Email',
            required: true
          }, {
            field: 'Phone',
            required: false
          }, {
            field: 'Move-in Timeline',
            required: false
          }, {
            field: 'Budget Range',
            required: false
          }].map(item => <div key={item.field} className="flex items-center justify-between" data-id="element-6885">
                <span className="text-sm text-slate-700" data-id="element-6886">{item.field}</span>
                <div className="flex items-center gap-4" data-id="element-6887">
                  <label className="flex items-center gap-2 text-sm text-slate-600" data-id="element-6888">
                    <input type="checkbox" defaultChecked={item.required} onChange={onChange} data-id="element-6889" />
                    Required
                  </label>
                  <Toggle defaultChecked onChange={onChange} data-id="element-6890" />
                </div>
              </div>)}
          </div>
        </CardContent>
      </Card>
    </div>;
}
// User Management Settings
function UserManagementSettings({
  onChange
}: {
  onChange: () => void;
}) {
  return <div className="space-y-6" data-id="element-6891">
      <div className="flex items-center justify-between" data-id="element-6892">
        <div data-id="element-6893">
          <h1 className="text-2xl font-bold text-slate-900" data-id="element-6894">User Management</h1>
          <p className="text-slate-500" data-id="element-6895">Manage team members and permissions</p>
        </div>
        <Button leftIcon={<Users className="h-4 w-4" data-id="element-6897" />} data-id="element-6896">Invite User</Button>
      </div>

      <Card data-id="element-6898">
        <CardHeader data-id="element-6899">
          <CardTitle data-id="element-6900">Team Members</CardTitle>
        </CardHeader>
        <CardContent data-id="element-6901">
          <div className="space-y-3" data-id="element-6902">
            {[{
            name: 'John Doe',
            email: 'john@brookfield.com',
            role: 'Admin'
          }, {
            name: 'Sarah Johnson',
            email: 'sarah@brookfield.com',
            role: 'Editor'
          }, {
            name: 'Michael Chen',
            email: 'michael@brookfield.com',
            role: 'Viewer'
          }].map(user => <div key={user.email} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg" data-id="element-6903">
                <div data-id="element-6904">
                  <p className="font-medium text-slate-900" data-id="element-6905">{user.name}</p>
                  <p className="text-sm text-slate-500" data-id="element-6906">{user.email}</p>
                </div>
                <div className="flex items-center gap-3" data-id="element-6907">
                  <Badge variant="neutral" data-id="element-6908">{user.role}</Badge>
                  <Button variant="ghost" size="sm" data-id="element-6909">
                    Edit
                  </Button>
                </div>
              </div>)}
          </div>
        </CardContent>
      </Card>

      <Card data-id="element-6910">
        <CardHeader data-id="element-6911">
          <CardTitle data-id="element-6912">Role Permissions</CardTitle>
        </CardHeader>
        <CardContent data-id="element-6913">
          <div className="space-y-4" data-id="element-6914">
            <div className="grid grid-cols-4 gap-4 text-sm font-medium text-slate-600 pb-2 border-b" data-id="element-6915">
              <div data-id="element-6916">Permission</div>
              <div className="text-center" data-id="element-6917">Admin</div>
              <div className="text-center" data-id="element-6918">Editor</div>
              <div className="text-center" data-id="element-6919">Viewer</div>
            </div>
            {['Manage Projects', 'Edit Options', 'View Analytics', 'Manage Users', 'Export Data'].map(permission => <div key={permission} className="grid grid-cols-4 gap-4 items-center" data-id="element-6920">
                <div className="text-sm text-slate-700" data-id="element-6921">{permission}</div>
                <div className="flex justify-center" data-id="element-6922">
                  <input type="checkbox" defaultChecked className="h-4 w-4" data-id="element-6923" />
                </div>
                <div className="flex justify-center" data-id="element-6924">
                  <input type="checkbox" defaultChecked={permission !== 'Manage Users'} className="h-4 w-4" data-id="element-6925" />
                </div>
                <div className="flex justify-center" data-id="element-6926">
                  <input type="checkbox" defaultChecked={permission === 'View Analytics'} className="h-4 w-4" data-id="element-6927" />
                </div>
              </div>)}
          </div>
        </CardContent>
      </Card>
    </div>;
}
// Notification Settings - WITH WORKING TOGGLES
function NotificationSettings({
  notifications,
  onToggle
}: {
  notifications: {
    newBuyer: boolean;
    tourCompletion: boolean;
    optionSelections: boolean;
    dailyDigest: boolean;
    weeklyReport: boolean;
  };
  onToggle: (key: keyof typeof notifications) => void;
}) {
  return <div className="space-y-6" data-id="element-6928">
      <div data-id="element-6929">
        <h1 className="text-2xl font-bold text-slate-900" data-id="element-6930">Notifications</h1>
        <p className="text-slate-500" data-id="element-6931">
          Configure email alerts and notifications
        </p>
      </div>

      <Card data-id="element-6932">
        <CardHeader data-id="element-6933">
          <CardTitle data-id="element-6934">Email Notifications</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4" data-id="element-6935">
          <div className="flex items-center justify-between py-3 border-b border-slate-100" data-id="element-6936">
            <div data-id="element-6937">
              <p className="font-medium text-slate-900" data-id="element-6938">
                New Buyer Registration
              </p>
              <p className="text-sm text-slate-500" data-id="element-6939">
                Get notified when someone starts a tour
              </p>
            </div>
            <Toggle checked={notifications.newBuyer} onChange={() => onToggle('newBuyer')} data-id="element-6940" />
          </div>
          <div className="flex items-center justify-between py-3 border-b border-slate-100" data-id="element-6941">
            <div data-id="element-6942">
              <p className="font-medium text-slate-900" data-id="element-6943">Tour Completion</p>
              <p className="text-sm text-slate-500" data-id="element-6944">
                Alert when a buyer completes their tour
              </p>
            </div>
            <Toggle checked={notifications.tourCompletion} onChange={() => onToggle('tourCompletion')} data-id="element-6945" />
          </div>
          <div className="flex items-center justify-between py-3 border-b border-slate-100" data-id="element-6946">
            <div data-id="element-6947">
              <p className="font-medium text-slate-900" data-id="element-6948">Option Selections</p>
              <p className="text-sm text-slate-500" data-id="element-6949">
                Notify when buyers select upgrades
              </p>
            </div>
            <Toggle checked={notifications.optionSelections} onChange={() => onToggle('optionSelections')} data-id="element-6950" />
          </div>
          <div className="flex items-center justify-between py-3 border-b border-slate-100" data-id="element-6951">
            <div data-id="element-6952">
              <p className="font-medium text-slate-900" data-id="element-6953">Daily Digest</p>
              <p className="text-sm text-slate-500" data-id="element-6954">
                Summary of all activity sent daily at 9 AM
              </p>
            </div>
            <Toggle checked={notifications.dailyDigest} onChange={() => onToggle('dailyDigest')} data-id="element-6955" />
          </div>
          <div className="flex items-center justify-between py-3" data-id="element-6956">
            <div data-id="element-6957">
              <p className="font-medium text-slate-900" data-id="element-6958">Weekly Report</p>
              <p className="text-sm text-slate-500" data-id="element-6959">
                Comprehensive analytics every Monday
              </p>
            </div>
            <Toggle checked={notifications.weeklyReport} onChange={() => onToggle('weeklyReport')} data-id="element-6960" />
          </div>
        </CardContent>
      </Card>

      <Card data-id="element-6961">
        <CardHeader data-id="element-6962">
          <CardTitle data-id="element-6963">Notification Recipients</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4" data-id="element-6964">
          <Input label="Primary Email" defaultValue="notifications@brookfield.com" data-id="element-6965" />
          <div data-id="element-6966">
            <label className="block text-sm font-medium text-slate-700 mb-2" data-id="element-6967">
              Additional Recipients
            </label>
            <div className="space-y-2" data-id="element-6968">
              <Input placeholder="email@example.com" data-id="element-6969" />
              <Button variant="outline" size="sm" data-id="element-6970">
                Add Recipient
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>;
}
// Mortgage Calculator Settings
function MortgageCalculatorSettings({
  onChange
}: {
  onChange: () => void;
}) {
  return <div className="space-y-6" data-id="element-6971">
      <div data-id="element-6972">
        <h1 className="text-2xl font-bold text-slate-900" data-id="element-6973">
          Mortgage Calculator
        </h1>
        <p className="text-slate-500" data-id="element-6974">
          Configure default mortgage calculation values
        </p>
      </div>

      <Card data-id="element-6975">
        <CardHeader data-id="element-6976">
          <CardTitle data-id="element-6977">Default Values</CardTitle>
        </CardHeader>
        <CardContent className="space-y-6" data-id="element-6978">
          <p className="text-sm text-slate-600 bg-slate-50 p-4 rounded-lg" data-id="element-6979">
            These values will be used as defaults in the buyer's mortgage
            calculator. Buyers can override them in their own view.
          </p>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-6980">
            <Input label="Default Interest Rate (%)" defaultValue="6.5" type="number" step="0.1" onChange={onChange} data-id="element-6981" />
            <Input label="Down Payment (%)" defaultValue="20" type="number" step="1" onChange={onChange} data-id="element-6982" />
            <Input label="Loan Term (Years)" defaultValue="30" type="number" step="5" onChange={onChange} data-id="element-6983" />
            <Input label="Property Tax Rate (%)" defaultValue="1.2" type="number" step="0.1" onChange={onChange} data-id="element-6984" />
            <Input label="Home Insurance (Annual $)" defaultValue="1200" type="number" onChange={onChange} data-id="element-6985" />
            <Input label="HOA Fees (Monthly $)" defaultValue="150" type="number" onChange={onChange} data-id="element-6986" />
          </div>
        </CardContent>
      </Card>
    </div>;
}
// Pricing & Display Settings
function PricingDisplaySettings({
  onChange
}: {
  onChange: () => void;
}) {
  return <div className="space-y-6" data-id="element-6987">
      <div data-id="element-6988">
        <h1 className="text-2xl font-bold text-slate-900" data-id="element-6989">Pricing & Display</h1>
        <p className="text-slate-500" data-id="element-6990">Control how pricing is shown to buyers</p>
      </div>

      <Card data-id="element-6991">
        <CardHeader data-id="element-6992">
          <CardTitle data-id="element-6993">Price Visibility</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4" data-id="element-6994">
          <div className="flex items-center justify-between py-3 border-b border-slate-100" data-id="element-6995">
            <div data-id="element-6996">
              <p className="font-medium text-slate-900" data-id="element-6997">Show Base Prices</p>
              <p className="text-sm text-slate-500" data-id="element-6998">
                Display model starting prices
              </p>
            </div>
            <Toggle defaultChecked onChange={onChange} data-id="element-6999" />
          </div>
          <div className="flex items-center justify-between py-3 border-b border-slate-100" data-id="element-7000">
            <div data-id="element-7001">
              <p className="font-medium text-slate-900" data-id="element-7002">Show Option Prices</p>
              <p className="text-sm text-slate-500" data-id="element-7003">Display upgrade pricing</p>
            </div>
            <Toggle defaultChecked onChange={onChange} data-id="element-7004" />
          </div>
          <div className="flex items-center justify-between py-3" data-id="element-7005">
            <div data-id="element-7006">
              <p className="font-medium text-slate-900" data-id="element-7007">Show Running Total</p>
              <p className="text-sm text-slate-500" data-id="element-7008">
                Display cumulative price as buyers select options
              </p>
            </div>
            <Toggle defaultChecked onChange={onChange} data-id="element-7009" />
          </div>
        </CardContent>
      </Card>

      <Card data-id="element-7010">
        <CardHeader data-id="element-7011">
          <CardTitle data-id="element-7012">Price Format</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4" data-id="element-7013">
          <div data-id="element-7014">
            <label className="block text-sm font-medium text-slate-700 mb-2" data-id="element-7015">
              Currency Symbol
            </label>
            <select className="w-full px-3 py-2 border border-slate-300 rounded-md focus:ring-primary-500 focus:border-primary-500" onChange={onChange} data-id="element-7016">
              <option value="$" data-id="element-7017">$ (USD)</option>
              <option value="€" data-id="element-7018">€ (EUR)</option>
              <option value="£" data-id="element-7019">£ (GBP)</option>
              <option value="$" data-id="element-7020">$ (CAD)</option>
            </select>
          </div>
          <div className="flex items-center justify-between" data-id="element-7021">
            <div data-id="element-7022">
              <p className="font-medium text-slate-900" data-id="element-7023">
                Include Tax in Prices
              </p>
              <p className="text-sm text-slate-500" data-id="element-7024">
                Show tax-inclusive pricing
              </p>
            </div>
            <Toggle onChange={onChange} data-id="element-7025" />
          </div>
          <Input label="Tax Rate (%)" defaultValue="7.5" type="number" step="0.1" onChange={onChange} data-id="element-7026" />
        </CardContent>
      </Card>
    </div>;
}
// PDF Summary Settings
function PDFSummarySettings({
  onChange
}: {
  onChange: () => void;
}) {
  return <div className="space-y-6" data-id="element-7027">
      <div data-id="element-7028">
        <h1 className="text-2xl font-bold text-slate-900" data-id="element-7029">PDF Summaries</h1>
        <p className="text-slate-500" data-id="element-7030">Customize buyer PDF exports</p>
      </div>

      <Card data-id="element-7031">
        <CardHeader data-id="element-7032">
          <CardTitle data-id="element-7033">Branding</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4" data-id="element-7034">
          <div className="flex items-center justify-between" data-id="element-7035">
            <div data-id="element-7036">
              <p className="font-medium text-slate-900" data-id="element-7037">Include Company Logo</p>
              <p className="text-sm text-slate-500" data-id="element-7038">Show logo on PDF header</p>
            </div>
            <Toggle defaultChecked onChange={onChange} data-id="element-7039" />
          </div>
          <div className="flex items-center justify-between" data-id="element-7040">
            <div data-id="element-7041">
              <p className="font-medium text-slate-900" data-id="element-7042">Include Contact Info</p>
              <p className="text-sm text-slate-500" data-id="element-7043">
                Add company details to footer
              </p>
            </div>
            <Toggle defaultChecked onChange={onChange} data-id="element-7044" />
          </div>
        </CardContent>
      </Card>

      <Card data-id="element-7045">
        <CardHeader data-id="element-7046">
          <CardTitle data-id="element-7047">Content Options</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4" data-id="element-7048">
          <div className="flex items-center justify-between py-3 border-b border-slate-100" data-id="element-7049">
            <span className="text-sm text-slate-700" data-id="element-7050">Model Details</span>
            <Toggle defaultChecked onChange={onChange} data-id="element-7051" />
          </div>
          <div className="flex items-center justify-between py-3 border-b border-slate-100" data-id="element-7052">
            <span className="text-sm text-slate-700" data-id="element-7053">Selected Options</span>
            <Toggle defaultChecked onChange={onChange} data-id="element-7054" />
          </div>
          <div className="flex items-center justify-between py-3 border-b border-slate-100" data-id="element-7055">
            <span className="text-sm text-slate-700" data-id="element-7056">Pricing Breakdown</span>
            <Toggle defaultChecked onChange={onChange} data-id="element-7057" />
          </div>
          <div className="flex items-center justify-between py-3 border-b border-slate-100" data-id="element-7058">
            <span className="text-sm text-slate-700" data-id="element-7059">Mortgage Estimate</span>
            <Toggle defaultChecked onChange={onChange} data-id="element-7060" />
          </div>
          <div className="flex items-center justify-between py-3" data-id="element-7061">
            <span className="text-sm text-slate-700" data-id="element-7062">Option Images</span>
            <Toggle defaultChecked onChange={onChange} data-id="element-7063" />
          </div>
        </CardContent>
      </Card>

      <Card data-id="element-7064">
        <CardHeader data-id="element-7065">
          <CardTitle data-id="element-7066">Disclaimer Text</CardTitle>
        </CardHeader>
        <CardContent data-id="element-7067">
          <textarea className="w-full px-3 py-2 border border-slate-300 rounded-md focus:ring-primary-500 focus:border-primary-500" rows={4} defaultValue="Prices and specifications are subject to change without notice. Please contact us for the most current information." onChange={onChange} data-id="element-7068" />
        </CardContent>
      </Card>
    </div>;
}
// Integration Settings - REORDERED WITH ERP ADDED
function IntegrationSettings({
  onChange
}: {
  onChange: () => void;
}) {
  return <div className="space-y-6" data-id="element-7069">
      <div data-id="element-7070">
        <h1 className="text-2xl font-bold text-slate-900" data-id="element-7071">Integrations</h1>
        <p className="text-slate-500" data-id="element-7072">Connect with third-party services</p>
      </div>

      {/* CRM Integration */}
      <Card data-id="element-7073">
        <CardHeader data-id="element-7074">
          <CardTitle data-id="element-7075">CRM Integration</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4" data-id="element-7076">
          <div data-id="element-7077">
            <label className="block text-sm font-medium text-slate-700 mb-2" data-id="element-7078">
              Select CRM
            </label>
            <select className="w-full px-3 py-2 border border-slate-300 rounded-md focus:ring-primary-500 focus:border-primary-500" onChange={onChange} data-id="element-7079">
              <option value="" data-id="element-7080">None</option>
              <option value="salesforce" data-id="element-7081">Salesforce</option>
              <option value="hubspot" data-id="element-7082">HubSpot</option>
              <option value="zoho" data-id="element-7083">Zoho CRM</option>
              <option value="pipedrive" data-id="element-7084">Pipedrive</option>
            </select>
          </div>
          <Input label="API Key" type="password" placeholder="Enter API key" onChange={onChange} data-id="element-7085" />
          <Button variant="outline" data-id="element-7086">Test Connection</Button>
        </CardContent>
      </Card>

      {/* ERP Integration - NEW */}
      <Card data-id="element-7087">
        <CardHeader data-id="element-7088">
          <CardTitle data-id="element-7089">ERP Integration</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4" data-id="element-7090">
          <div data-id="element-7091">
            <label className="block text-sm font-medium text-slate-700 mb-2" data-id="element-7092">
              Select ERP System
            </label>
            <select className="w-full px-3 py-2 border border-slate-300 rounded-md focus:ring-primary-500 focus:border-primary-500" onChange={onChange} data-id="element-7093">
              <option value="" data-id="element-7094">None</option>
              <option value="sap" data-id="element-7095">SAP</option>
              <option value="oracle" data-id="element-7096">Oracle NetSuite</option>
              <option value="microsoft" data-id="element-7097">Microsoft Dynamics 365</option>
              <option value="sage" data-id="element-7098">Sage Intacct</option>
              <option value="acumatica" data-id="element-7099">Acumatica</option>
            </select>
          </div>
          <Input label="API Endpoint" placeholder="https://your-erp.com/api" onChange={onChange} data-id="element-7100" />
          <Input label="API Key" type="password" placeholder="Enter API key" onChange={onChange} data-id="element-7101" />
          <div className="flex items-center justify-between" data-id="element-7102">
            <div data-id="element-7103">
              <p className="font-medium text-slate-900" data-id="element-7104">
                Sync Orders Automatically
              </p>
              <p className="text-sm text-slate-500" data-id="element-7105">
                Push buyer selections to ERP
              </p>
            </div>
            <Toggle onChange={onChange} data-id="element-7106" />
          </div>
          <Button variant="outline" data-id="element-7107">Test Connection</Button>
        </CardContent>
      </Card>

      {/* Calendar Booking - MOVED UP */}
      <Card data-id="element-7108">
        <CardHeader data-id="element-7109">
          <CardTitle data-id="element-7110">Calendar Booking</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4" data-id="element-7111">
          <div data-id="element-7112">
            <label className="block text-sm font-medium text-slate-700 mb-2" data-id="element-7113">
              Select Platform
            </label>
            <select className="w-full px-3 py-2 border border-slate-300 rounded-md focus:ring-primary-500 focus:border-primary-500" onChange={onChange} data-id="element-7114">
              <option value="" data-id="element-7115">None</option>
              <option value="calendly" data-id="element-7116">Calendly</option>
              <option value="acuity" data-id="element-7117">Acuity Scheduling</option>
              <option value="cal" data-id="element-7118">Cal.com</option>
            </select>
          </div>
          <Input label="Booking Link" placeholder="https://calendly.com/..." onChange={onChange} data-id="element-7119" />
        </CardContent>
      </Card>

      {/* Email Marketing */}
      <Card data-id="element-7120">
        <CardHeader data-id="element-7121">
          <CardTitle data-id="element-7122">Email Marketing</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4" data-id="element-7123">
          <div data-id="element-7124">
            <label className="block text-sm font-medium text-slate-700 mb-2" data-id="element-7125">
              Select Platform
            </label>
            <select className="w-full px-3 py-2 border border-slate-300 rounded-md focus:ring-primary-500 focus:border-primary-500" onChange={onChange} data-id="element-7126">
              <option value="" data-id="element-7127">None</option>
              <option value="mailchimp" data-id="element-7128">Mailchimp</option>
              <option value="constant-contact" data-id="element-7129">Constant Contact</option>
              <option value="sendgrid" data-id="element-7130">SendGrid</option>
              <option value="klaviyo" data-id="element-7131">Klaviyo</option>
            </select>
          </div>
          <Input label="API Key" type="password" placeholder="Enter API key" onChange={onChange} data-id="element-7132" />
          <div className="flex items-center justify-between" data-id="element-7133">
            <div data-id="element-7134">
              <p className="font-medium text-slate-900" data-id="element-7135">Auto-sync New Buyers</p>
              <p className="text-sm text-slate-500" data-id="element-7136">
                Add to mailing list automatically
              </p>
            </div>
            <Toggle onChange={onChange} data-id="element-7137" />
          </div>
        </CardContent>
      </Card>

      {/* Webhooks */}
      <Card data-id="element-7138">
        <CardHeader data-id="element-7139">
          <CardTitle data-id="element-7140">Webhooks</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4" data-id="element-7141">
          <p className="text-sm text-slate-600" data-id="element-7142">
            Send real-time data to your custom endpoints
          </p>
          <Input label="Webhook URL" placeholder="https://your-domain.com/webhook" onChange={onChange} data-id="element-7143" />
          <div data-id="element-7144">
            <label className="block text-sm font-medium text-slate-700 mb-2" data-id="element-7145">
              Trigger Events
            </label>
            <div className="space-y-2" data-id="element-7146">
              {['New Buyer', 'Tour Completed', 'Option Selected', 'PDF Downloaded'].map(event => <label key={event} className="flex items-center gap-2" data-id="element-7147">
                  <input type="checkbox" onChange={onChange} data-id="element-7148" />
                  <span className="text-sm text-slate-700" data-id="element-7149">{event}</span>
                </label>)}
            </div>
          </div>
        </CardContent>
      </Card>
    </div>;
}
// Analytics & Tracking Settings
function AnalyticsTrackingSettings({
  onChange
}: {
  onChange: () => void;
}) {
  return <div className="space-y-6" data-id="element-7150">
      <div data-id="element-7151">
        <h1 className="text-2xl font-bold text-slate-900" data-id="element-7152">
          Analytics & Tracking
        </h1>
        <p className="text-slate-500" data-id="element-7153">
          Configure tracking and analytics integrations
        </p>
      </div>

      <Card data-id="element-7154">
        <CardHeader data-id="element-7155">
          <CardTitle data-id="element-7156">Google Analytics</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4" data-id="element-7157">
          <div className="flex items-center justify-between mb-4" data-id="element-7158">
            <div data-id="element-7159">
              <p className="font-medium text-slate-900" data-id="element-7160">
                Enable Google Analytics
              </p>
              <p className="text-sm text-slate-500" data-id="element-7161">
                Track buyer behavior and conversions
              </p>
            </div>
            <Toggle onChange={onChange} data-id="element-7162" />
          </div>
          <Input label="Measurement ID" placeholder="G-XXXXXXXXXX" onChange={onChange} data-id="element-7163" />
        </CardContent>
      </Card>

      <Card data-id="element-7164">
        <CardHeader data-id="element-7165">
          <CardTitle data-id="element-7166">Meta Pixel</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4" data-id="element-7167">
          <div className="flex items-center justify-between mb-4" data-id="element-7168">
            <div data-id="element-7169">
              <p className="font-medium text-slate-900" data-id="element-7170">Enable Meta Pixel</p>
              <p className="text-sm text-slate-500" data-id="element-7171">
                Track Facebook ad conversions
              </p>
            </div>
            <Toggle onChange={onChange} data-id="element-7172" />
          </div>
          <Input label="Pixel ID" placeholder="123456789012345" onChange={onChange} data-id="element-7173" />
        </CardContent>
      </Card>

      <Card data-id="element-7174">
        <CardHeader data-id="element-7175">
          <CardTitle data-id="element-7176">Custom Events</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4" data-id="element-7177">
          <div className="flex items-center justify-between py-3 border-b border-slate-100" data-id="element-7178">
            <span className="text-sm text-slate-700" data-id="element-7179">Track Model Views</span>
            <Toggle defaultChecked onChange={onChange} data-id="element-7180" />
          </div>
          <div className="flex items-center justify-between py-3 border-b border-slate-100" data-id="element-7181">
            <span className="text-sm text-slate-700" data-id="element-7182">
              Track Option Selections
            </span>
            <Toggle defaultChecked onChange={onChange} data-id="element-7183" />
          </div>
          <div className="flex items-center justify-between py-3 border-b border-slate-100" data-id="element-7184">
            <span className="text-sm text-slate-700" data-id="element-7185">Track PDF Downloads</span>
            <Toggle defaultChecked onChange={onChange} data-id="element-7186" />
          </div>
          <div className="flex items-center justify-between py-3" data-id="element-7187">
            <span className="text-sm text-slate-700" data-id="element-7188">
              Track Tour Completions
            </span>
            <Toggle defaultChecked onChange={onChange} data-id="element-7189" />
          </div>
        </CardContent>
      </Card>

      <Card data-id="element-7190">
        <CardHeader data-id="element-7191">
          <CardTitle data-id="element-7192">Data Privacy</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4" data-id="element-7193">
          <Input label="Data Retention Period (days)" defaultValue="365" type="number" onChange={onChange} data-id="element-7194" />
          <div className="flex items-center justify-between" data-id="element-7195">
            <div data-id="element-7196">
              <p className="font-medium text-slate-900" data-id="element-7197">
                Cookie Consent Banner
              </p>
              <p className="text-sm text-slate-500" data-id="element-7198">
                Show GDPR/CCPA compliance notice
              </p>
            </div>
            <Toggle defaultChecked onChange={onChange} data-id="element-7199" />
          </div>
        </CardContent>
      </Card>
    </div>;
}
// Content Management Settings
function ContentManagementSettings({
  onChange
}: {
  onChange: () => void;
}) {
  return <div className="space-y-6" data-id="element-7200">
      <div data-id="element-7201">
        <h1 className="text-2xl font-bold text-slate-900" data-id="element-7202">
          Content Management
        </h1>
        <p className="text-slate-500" data-id="element-7203">Manage media and content settings</p>
      </div>

      <Card data-id="element-7204">
        <CardHeader data-id="element-7205">
          <CardTitle data-id="element-7206">Default Images</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4" data-id="element-7207">
          <div data-id="element-7208">
            <label className="block text-sm font-medium text-slate-700 mb-2" data-id="element-7209">
              Placeholder Image for Options
            </label>
            <div className="flex items-center gap-4" data-id="element-7210">
              <div className="h-20 w-20 bg-slate-100 rounded-lg flex items-center justify-center border border-slate-200" data-id="element-7211">
                <Image className="h-8 w-8 text-slate-400" data-id="element-7212" />
              </div>
              <Button variant="outline" leftIcon={<Upload className="h-4 w-4" data-id="element-7214" />} data-id="element-7213">
                Upload Image
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card data-id="element-7215">
        <CardHeader data-id="element-7216">
          <CardTitle data-id="element-7217">Media Library</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4" data-id="element-7218">
          <div className="grid grid-cols-2 gap-4" data-id="element-7219">
            <div className="p-4 bg-slate-50 rounded-lg" data-id="element-7220">
              <p className="text-2xl font-bold text-slate-900" data-id="element-7221">248</p>
              <p className="text-sm text-slate-500" data-id="element-7222">Total Images</p>
            </div>
            <div className="p-4 bg-slate-50 rounded-lg" data-id="element-7223">
              <p className="text-2xl font-bold text-slate-900" data-id="element-7224">1.2 GB</p>
              <p className="text-sm text-slate-500" data-id="element-7225">Storage Used</p>
            </div>
          </div>
          <Button variant="outline" className="w-full" data-id="element-7226">
            Manage Media Library
          </Button>
        </CardContent>
      </Card>

      <Card data-id="element-7227">
        <CardHeader data-id="element-7228">
          <CardTitle data-id="element-7229">Upload Limits</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4" data-id="element-7230">
          <Input label="Max File Size (MB)" defaultValue="10" type="number" onChange={onChange} data-id="element-7231" />
          <div data-id="element-7232">
            <label className="block text-sm font-medium text-slate-700 mb-2" data-id="element-7233">
              Allowed File Types
            </label>
            <div className="space-y-2" data-id="element-7234">
              {['JPG/JPEG', 'PNG', 'WebP', 'SVG', 'GIF'].map(type => <label key={type} className="flex items-center gap-2" data-id="element-7235">
                  <input type="checkbox" defaultChecked onChange={onChange} data-id="element-7236" />
                  <span className="text-sm text-slate-700" data-id="element-7237">{type}</span>
                </label>)}
            </div>
          </div>
        </CardContent>
      </Card>

      <Card data-id="element-7238">
        <CardHeader data-id="element-7239">
          <CardTitle data-id="element-7240">Video Settings</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4" data-id="element-7241">
          <div className="flex items-center justify-between" data-id="element-7242">
            <div data-id="element-7243">
              <p className="font-medium text-slate-900" data-id="element-7244">Enable Video Tours</p>
              <p className="text-sm text-slate-500" data-id="element-7245">
                Allow video content in tours
              </p>
            </div>
            <Toggle defaultChecked onChange={onChange} data-id="element-7246" />
          </div>
          <div className="flex items-center justify-between" data-id="element-7247">
            <div data-id="element-7248">
              <p className="font-medium text-slate-900" data-id="element-7249">Autoplay Videos</p>
              <p className="text-sm text-slate-500" data-id="element-7250">
                Start videos automatically
              </p>
            </div>
            <Toggle onChange={onChange} data-id="element-7251" />
          </div>
        </CardContent>
      </Card>
    </div>;
}